import { ref, computed } from 'vue'
import type { DirectoryItem, DirectoryItemType, DirectoryItemUpdate } from './types'

// Helper to create a deep copy of the tree data, with cycle detection
function deepCopy<T>(obj: T, seen = new WeakMap()): T {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  // Handle cycles
  if (seen.has(obj)) {
    return seen.get(obj)
  }

  // Handle Date objects
  if (obj instanceof Date) {
    const newDate = new Date(obj.getTime())
    seen.set(obj, newDate)
    return newDate as any
  }

  // Handle Array objects
  if (Array.isArray(obj)) {
    const newArr: any[] = []
    seen.set(obj, newArr) // Store the reference before recursing
    obj.forEach((item, i) => {
      newArr[i] = deepCopy(item, seen)
    })
    return newArr as any
  }

  // Handle Files
  if (obj instanceof File) {
    const newFile = new File([obj], obj.name, {
      type: obj.type,
      lastModified: obj.lastModified
    })
    seen.set(obj, newFile)
    return newFile as any
  }

  // Handle Objects
  if (obj instanceof Object) {
    const newObj = Object.create(Object.getPrototypeOf(obj))
    seen.set(obj, newObj)
    for (const key of Object.keys(obj)) {
      newObj[key as keyof T] = deepCopy(obj[key as keyof T], seen)
    }
    return newObj
  }

  // Fallback, though typically not reached
  return obj
}

export const defaultCreateRoot = <E extends Partial<object>>(): DirectoryItem<'directory', E> =>
  ({
    id: 'root',
    name: 'Root',
    type: 'directory',
    children: []
  } as DirectoryItem<'directory', E>)

export function useDirectoryTree<E extends Partial<object> = {}>() {
  const treeData = ref<DirectoryItem<'directory', E>>(defaultCreateRoot<E>())
  const readonlyTree = computed<DirectoryItem<'directory', E>>(() =>
    deepCopy<DirectoryItem<'directory', E>>(treeData.value)
  )

  function resetTree(): void {
    treeData.value = deepCopy<DirectoryItem<'directory', E>>(defaultCreateRoot<E>())
  }

  function getRootNode(): DirectoryItem<'directory', E> {
    return treeData.value
  }

  function _findNodeById(
    startNode: DirectoryItem<DirectoryItemType, E> | null,
    id: string
  ): DirectoryItem<DirectoryItemType, E> | null {
    if (!startNode) return null
    if (startNode.id === id) return startNode

    if (startNode.type === 'directory' && startNode.children) {
      for (const child of startNode.children) {
        const found = _findNodeById(child, id)
        if (found) return found
      }
    }
    return null
  }

  function findNodeById(id: string): DirectoryItem<DirectoryItemType, E> | null {
    return _findNodeById(treeData.value, id)
  }

  function getChildrenOfNodeById(id: string): DirectoryItem<DirectoryItemType, E>[] {
    const node = _findNodeById(treeData.value, id)
    if (node && node.type === 'directory' && node.children) {
      return node.children as DirectoryItem<DirectoryItemType, E>[]
    }
    return []
  }

  function updateParentIds(
    node: DirectoryItem<DirectoryItemType, E>,
    parentId: string | null = null
  ): void {
    node.parentId = parentId
    if (node.type === 'directory' && node.children) {
      for (const child of node.children) {
        updateParentIds(child, node.id)
      }
    }
  }

  function addNode(
    parentId: string,
    itemData: DirectoryItem
  ): DirectoryItem<DirectoryItemType, E> | null {
    const parentNode = _findNodeById(treeData.value, parentId)
    if (!parentNode || parentNode.type !== 'directory') {
      console.error('Parent not found or not a directory:', parentId)
      return null
    }

    if (!parentNode.children) {
      parentNode.children = []
    }

    const newNodeBase = {
      parentId: parentId,
      ...itemData
    }

    let newNode: DirectoryItem<DirectoryItemType, E>

    if (itemData.type === 'directory') {
      newNode = {
        ...newNodeBase,
        type: 'directory'
      } as DirectoryItem<'directory', E>
      newNode.children ??= []

      updateParentIds(newNode, parentId)
    } else if (itemData.type === 'file') {
      newNode = { ...newNodeBase, type: 'file' } as DirectoryItem<'file', E>
    } else if (itemData.type === 'link') {
      if (!itemData.url) {
        console.error('URL is required for link type')
        return null
      }
      newNode = { ...newNodeBase, type: 'link', url: itemData.url } as DirectoryItem<'link', E>
    } else {
      console.error('Invalid item type for new node:', (itemData as any).type)
      return null
    }

    parentNode.children.push(newNode)
    return deepCopy(newNode) // Return a copy
  }

  function findParentAndRemoveChild(
    currentNode: DirectoryItem<DirectoryItemType, E>,
    childId: string
  ): boolean {
    if (currentNode.type === 'directory' && currentNode.children) {
      const index = currentNode.children.findIndex((child) => child.id === childId)
      if (index !== -1) {
        currentNode.children.splice(index, 1)
        return true
      }
      for (const child of currentNode.children) {
        if (findParentAndRemoveChild(child, childId)) return true
      }
    }
    return false
  }

  function deleteNodeById(nodeId: string): boolean {
    if (nodeId === treeData.value.id) {
      console.error('Cannot delete root node')
      return false
    }
    return findParentAndRemoveChild(treeData.value, nodeId)
  }

  function deleteNodesByIds(nodeIds: string[]): boolean {
    if (nodeIds.length === 0) {
      console.warn('No node IDs provided for deletion.')
      return false
    }

    let allDeletesSuccessful = true
    for (const nodeId of nodeIds) {
      if (!deleteNodeById(nodeId)) {
        console.warn(`Failed to delete node with ID: ${nodeId}`)
        allDeletesSuccessful = false
      }
    }
    return allDeletesSuccessful
  }

  function updateNodeDetails(
    nodeId: string,
    updates: Omit<DirectoryItemUpdate<DirectoryItemType, E>, 'type'>
  ): DirectoryItem<DirectoryItemType, E> | null {
    const node = _findNodeById(treeData.value, nodeId)
    if (node) {
      // Defensive destructuring to ensure restricted properties are not assigned.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, parentId, children, type, ...restOfUpdates } = updates as any
      Object.assign(node, restOfUpdates)
      return deepCopy(node) as DirectoryItem<DirectoryItemType, E>
    }
    console.error('Node not found for update:', nodeId)
    return null
  }

  /**
   * Helper function to validate a single node move operation.
   * Checks for:
   * - Moving a node into itself.
   * - Node existence.
   * - Moving a node into one of its own descendants.
   */
  function _validateSingleNodeMove(
    nodeIdToMove: string,
    newParentId: string,
    newParentNode: DirectoryItem<'directory', E>
  ): { nodeToMove: DirectoryItem<DirectoryItemType, E> | null; error?: string } {
    if (nodeIdToMove === newParentId) {
      return { nodeToMove: null, error: `Cannot move item ${nodeIdToMove} into itself.` }
    }

    const nodeToMove = _findNodeById(treeData.value, nodeIdToMove)
    if (!nodeToMove) {
      return { nodeToMove: null, error: `Node to move not found: ${nodeIdToMove}` }
    }

    // Check if newParentNode (the target) is a descendant of nodeToMove (the source)
    let currentAncestor: DirectoryItem<DirectoryItemType, E> | null = newParentNode
    while (currentAncestor) {
      if (currentAncestor.id === nodeIdToMove) {
        return {
          nodeToMove: null,
          error: `Cannot move item ${nodeIdToMove} (a parent) into its own descendant ${newParentId}.`
        }
      }
      currentAncestor = currentAncestor.parentId
        ? _findNodeById(treeData.value, currentAncestor.parentId)
        : null
    }

    return { nodeToMove }
  }

  function moveNodes(nodeIds: string[], newParentId: string): boolean {
    // 1. Validate the new parent node
    const newParentNode = _findNodeById(treeData.value, newParentId)
    if (!newParentNode || newParentNode.type !== 'directory') {
      console.error('Target parent not found or not a directory:', newParentId)
      return false
    }
    newParentNode.children ??= []

    // Handle moving zero nodes (considered a success)
    if (nodeIds.length === 0) {
      return true
    }

    // 2. Validate each node to be moved and collect valid ones
    const validatedNodesData: { nodeId: string; node: DirectoryItem<DirectoryItemType, E> }[] = []
    let preValidationFailedOverall = false

    for (const nodeId of nodeIds) {
      const validationResult = _validateSingleNodeMove(
        nodeId,
        newParentId,
        newParentNode as DirectoryItem<'directory', E>
      )

      if (validationResult.error) {
        console.warn(validationResult.error)
        preValidationFailedOverall = true
      }

      if (validationResult.nodeToMove) {
        validatedNodesData.push({ nodeId, node: validationResult.nodeToMove })
      } else {
        // If nodeToMove is null (e.g., not found, or an error occurred during its validation),
        // mark overall pre-validation as failed.
        preValidationFailedOverall = true
      }
    }

    // 3. Abort if any pre-validation failed or if the number of valid nodes doesn't match the input
    if (preValidationFailedOverall || validatedNodesData.length !== nodeIds.length) {
      console.error(
        'Move operation aborted due to pre-check failures or some nodes not found/movable.'
      )
      return false
    }

    // 4. Perform the actual move operations
    let allMovesSuccessful = true
    for (const { node: nodeToMove } of validatedNodesData) {
      // Remove node from its old parent
      if (findParentAndRemoveChild(treeData.value, nodeToMove.id)) {
        // Add node to the new parent
        nodeToMove.parentId = newParentId
        newParentNode.children.push(nodeToMove)
      } else {
        // This should be rare if node was found, isn't root, and findParentAndRemoveChild is correct
        console.error('Failed to remove node from old parent during move:', nodeToMove.id)
        allMovesSuccessful = false
      }
    }

    return allMovesSuccessful
  }

  function setChildrenOfNodeById(
    id: string,
    children: DirectoryItem<DirectoryItemType, E>[]
  ): boolean {
    const node = _findNodeById(treeData.value, id)
    if (node && node.type === 'directory') {
      node.children = children
      return true
    }
    console.error('Node not found or not a directory:', id)
    return false
  }

  function addChildrenOfNodeById(
    id: string,
    children: DirectoryItem<DirectoryItemType, E>[]
  ): boolean {
    const node = _findNodeById(treeData.value, id)
    if (node && node.type === 'directory') {
      if (!node.children) {
        node.children = children
      } else {
        const existingChildren: DirectoryItem<DirectoryItemType, E>[] = node.children.filter(
          (child) => !children.some((c) => c.id === child.id)
        )
        node.children = [...existingChildren, ...children]
      }
      return true
    }
    console.error('Node not found or not a directory:', id)
    return false
  }

  function clearChildrenOfNodeById(id: string): boolean {
    const node = _findNodeById(treeData.value, id)
    if (node && node.type === 'directory') {
      node.children = []
      return true
    }
    console.error('Node not found or not a directory:', id)
    return false
  }

  return {
    tree: readonlyTree,
    resetTree,
    getRootNode,
    findNodeById,
    getChildrenOfNodeById,
    addNode,
    deleteNodeById,
    deleteNodesByIds,
    updateNodeDetails,
    moveNodes,
    setChildrenOfNodeById,
    addChildrenOfNodeById,
    clearChildrenOfNodeById
  }
}
